import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { MatchEvent } from 'shared/types/match-event';
import { ObservableField } from 'shared/utils/observer-field';
import { NewReceivedData, ReceivedEvent } from 'modules/core/domain/interfaces/supabase/received-data';
import supabase from '../supabase';

export type ConnectionType = 'CLOSED' | 'SUBSCRIBED' | 'TIMED_OUT' | 'CHANNEL_ERROR';

type TimeState = {
  seconds: number;
  period: number;
};

export type ScoreTeam = { id: number; score: number };
export type Score = [ScoreTeam, ScoreTeam];

export class MatchChronology {
  chanelEvents?: RealtimeChannel;
  connectionStatus: ObservableField<ConnectionType> = new ObservableField<ConnectionType>('CLOSED');
  events: ObservableField<MatchEvent[]> = new ObservableField<MatchEvent[]>([]);
  activeBoardId: ObservableField<number | null> = new ObservableField<number | null>(null);
  score: ObservableField<Score | null> = new ObservableField<Score | null>(null);
  currentTime: ObservableField<TimeState> = new ObservableField<TimeState>({
    seconds: 0,
    period: 0,
  });
  /**
   * `commit_timestamp` последнего применённого обновления журнала: видеометки (IMGM-1484) пишут его
   * рядом с позицией записи. После резервного опроса это время опроса, а не коммита.
   */
  lastCommitAt: ObservableField<string | null> = new ObservableField<string | null>(null);
  private readonly reportedDuplicatesFor = new Set<number>();

  constructor(private supabase: SupabaseClient) {}

  /**
   * Применить обновление строки журнала — и из realtime, и из резервного опроса.
   *
   * Дедупликации по `commit_timestamp` здесь нет намеренно. Была попытка
   * (`if (this.lastDateUpdate === date) return`), но она сравнивала объекты `Date` и не
   * срабатывала никогда; включённая «как задумано» она стала бы отбрасывать **разные**
   * обновления с одинаковой меткой в миллисекундах — секундный тик времени и гол в один
   * момент. Повторную доставку одного и того же значения и так гасит `ObservableField`
   * (уведомляет только на изменение по `!==`), а повторные события отсекает
   * `passedEventsId` в `use-init-subscribe-overlay-v2`.
   */
  public handleRecordUpdated(data: { new: NewReceivedData; commit_timestamp: string }) {
    console.debug({ event: 'new data received', data });

    // До `checkEvents`: подписчики событий читают метку того обновления, с которым событие пришло.
    this.lastCommitAt.value = data.commit_timestamp ?? null;
    this.checkEvents(data.new);
    this.checkActiveTemplate(data.new);
    this.checkTime(data.new);
    this.checkScore(data.new);
  }

  public async observeChanges(matchId: number) {
    this.chanelEvents = this.supabase
      .channel(`room${matchId}`)
      .on(
        // @ts-ignore
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'match_chronology',
          filter: `matchId=eq.${matchId}`,
        },
        this.handleRecordUpdated.bind(this),
      )
      .subscribe((status, err) => {
        console.debug({ status });

        if (err) {
          supabase.overlayLogs.createError({ comment: 'Не удалось подключиться к supabase', message: err.toString() });
          console.error({ err });
        }

        this.connectionStatus.value = status;
      }, 10000);
  }

  public async stopObserveChanges() {
    if (this.chanelEvents) {
      return await this.chanelEvents.unsubscribe();
    }
  }

  /**
   * Прочитать строку журнала матча. Оверлей журнал только читает — заводит его мобильное
   * приложение.
   *
   * Раньше здесь стоял `.single()`, а код `PGRST116` считался синонимом «записи нет» и вёл к
   * созданию новой. Но `.single()` отвечает этим кодом на **любое** количество строк, кроме
   * одной: и на ноль, и на несколько (`details` различаются, код — нет). Стоило двум читателям
   * одновременно открыть матч без журнала — оба создавали по строке, и дальше каждое чтение
   * видело «несколько», создавало ещё одну и возвращало её же: пустую, с `currentPeriod` = 1 и
   * `templateId` = null по умолчанию колонок. Отсюда все три симптома сразу — тайм прыгал между
   * живым значением из realtime и единицей, табло гасло, а таблица росла без предела, разгоняя
   * нагрузку на базу (у `matchId` нет ни уникального индекса, ни вообще индекса).
   *
   * Дубли не лечим на лету — их убирает миграция `20260821_match_chronology_unique_match.sql`;
   * пока они есть, берём самую раннюю строку. Ошибку это переживает: раз в секунду realtime
   * присылает актуальные значения.
   */
  public async getRow(matchId: number): Promise<NewReceivedData | null> {
    const { data, error } = await this.supabase
      .from('match_chronology')
      .select()
      .eq('matchId', matchId)
      .order('id', { ascending: true });

    if (error) {
      console.error(error);
      supabase.overlayLogs.createError({ comment: 'Не удалось получить данные из supabase', message: error.message });
      return null;
    }

    const rows = (data ?? []) as NewReceivedData[];
    if (rows.length === 0) return null;
    if (rows.length > 1) this.reportDuplicates(matchId, rows);

    return rows[0];
  }

  private checkEvents(data: NewReceivedData) {
    const chronology: ReceivedEvent[] = data.chronology;
    this.events.value = chronology.sort((a, b) => a.id - b.id);
  }

  private checkActiveTemplate(data: NewReceivedData) {
    this.activeBoardId.value = data.templateId;
  }

  private checkTime(data: NewReceivedData) {
    const { seconds, currentPeriod: period } = data;
    this.currentTime.value = {
      ...this.currentTime.value,
      seconds,
      period,
    };
  }

  private checkScore(data: NewReceivedData) {
    const { firstTeamScore, firstTeamId, secondTeamId, secondTeamScore } = data;
    this.score.value = [
      { id: firstTeamId, score: firstTeamScore },
      { id: secondTeamId, score: secondTeamScore },
    ];
  }

  /** Сообщить о дублях один раз на матч: иначе резервный опрос завалит журнал одинаковыми записями. */
  private reportDuplicates(matchId: number, rows: NewReceivedData[]) {
    if (this.reportedDuplicatesFor.has(matchId)) return;
    this.reportedDuplicatesFor.add(matchId);

    // eslint-disable-next-line lingui/no-unlocalized-strings -- запись журнала оверлея, её читает команда
    const message = `Матч ${matchId}: строк журнала ${rows.length} (id: ${rows.map((row) => row.id).join(', ')})`;
    console.warn({ event: 'duplicate match chronology rows', matchId, ids: rows.map((row) => row.id) });
    supabase.overlayLogs.createWarning({ comment: 'Дубли строк журнала матча', message });
  }
}
